import { useCallback, useMemo, useState } from 'react';

import { MaterialRequisitionDocumentResponse } from '@Types/api/form';
import { AnyObject } from 'antd/es/_util/type';
import { RowSelectionType } from 'antd/es/table/interface';
import { ViewTab } from '../../ViewTab.enum';
import ColumnHeader from './components/ColumnHeader';
import { ColumnSettings } from '../types';
import { ColumnDocumentDataType } from '../DataSource/type';

interface ColumnsProps {
  activeTab: keyof typeof ViewTab;
  dataSource: [];
}

const useColumnSettings = ({
  dataSource,
  activeTab,
}: ColumnsProps): ColumnSettings => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<
    React.Key[] | undefined
  >(undefined);
  const [selectedColumns, setSelectedRowColumns] = useState<
    Array<number | MaterialRequisitionDocumentResponse>
  >([]);

  const isSelected = Boolean(selectedRowKeys?.length);

  const selectedAllRows = useMemo(() => {
    if (dataSource?.length) {
      return selectedRowKeys?.length === dataSource?.length;
    }

    return false;
  }, [selectedRowKeys?.length, dataSource?.length]);

  const renderColumnTitle = useCallback(
    (originalNode: React.ReactNode) => (
      <div className={`${selectedAllRows ? 'trojan-selected-all' : ''}`}>
        {originalNode}
      </div>
    ),
    [selectedAllRows]
  );

  const rowSelection = useMemo(() => {
    return {
      onChange: (
        selectedKeys: React.Key[],
        selectedRowColumns: Array<AnyObject>
      ) => {
        const isDocument =
          selectedRowColumns.length &&
          Object.hasOwn(selectedRowColumns[0], 'document');

        const mappedItems = isDocument
          ? (selectedRowColumns as Array<ColumnDocumentDataType>).map(
              (item) => item.document
            )
          : (selectedKeys as unknown as number[]);

        setSelectedRowColumns(mappedItems);
        setSelectedRowKeys(selectedKeys);
      },
      fixed: true,
      columnTitle: renderColumnTitle,
      selectedRowKeys,
      type: 'checkbox' as RowSelectionType,
    };
  }, [renderColumnTitle, selectedRowKeys]);

  const renderColumnHeader = useCallback(
    (
      onDeleteItems: (
        items: Array<number | MaterialRequisitionDocumentResponse>
      ) => void
    ): JSX.Element => (
      <ColumnHeader
        activeTab={activeTab}
        isAllSelected={selectedAllRows}
        count={selectedRowKeys?.length || 0}
        onDelete={() => onDeleteItems(selectedColumns)}
        onDownload={() => null}
      />
    ),
    [selectedAllRows, selectedColumns, selectedRowKeys, activeTab]
  );

  return {
    isSelected,
    rowSelection,
    renderColumnHeader: (onDeleteItems: () => void) =>
      renderColumnHeader(onDeleteItems),
    clearState() {
      this.isSelected = false;
      setSelectedRowKeys([]);
      setSelectedRowColumns([]);
    },
  };
};

export default useColumnSettings;
